/**
 * Shared authenticated NestJS request helper for the Supabase→NestJS migration's
 * repository layer (`src/repositories/*`).
 *
 * Replaces the 10 byte-identical `nestRequest` copies that each did a bare
 * `fetch()` with NO timeout and NO retry — the cause of the intermittent
 * "Load failed (p1.dallali.com)" → silent Supabase fallback (DALLALI-FRONTEND-AJ).
 *
 * What this adds over the old per-repo copies:
 *  - **Timeout** via `AbortSignal.timeout` — a hung connection fails fast into the
 *    caller's fallback instead of hanging the dashboard on the browser default.
 *  - **Retry with backoff** on transient failures only (network errors, timeouts,
 *    and 5xx) — absorbs the single-blip case that produced most of the fallbacks.
 *    4xx are deterministic (the server answered) and are NEVER retried.
 *  - **Typed `NestRequestError`** carrying `status` / `kind` / `durationMs`, so the
 *    loud-fallback reporter (`reportNestFallback`) can escalate a genuine,
 *    retry-surviving failure to a Sentry *error* with real context.
 *
 * Usage — bind the slice once per repository, call sites stay unchanged:
 *   const nestRequest = makeNestRequest("contracts");
 *   const rows = await nestRequest<Row[]>(API_ROUTES.contracts.stats());
 */

import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { API_URL } from "@/services/api";
import { validateHeaderValue } from "@/lib/headerValidation";
import { clearNestRefreshToken, clearNestAccessToken } from "@/lib/authHelpers";

const API_KEY = import.meta.env.VITE_API_KEY || "";

/** Per-attempt request timeout. A dead/hung endpoint fails into the fallback in
 *  ~this long instead of hanging on the browser default (~60s+ in Safari). */
const DEFAULT_TIMEOUT_MS = 7000;
/** Retries AFTER the first attempt (so 3 total attempts max). Transient blips
 *  (the common "Load failed") fail fast, so retries usually cost only the backoff. */
const DEFAULT_RETRIES = 2;
/** Backoff base — attempt N waits BASE * N ms (250ms, 500ms). Short on purpose:
 *  the Supabase fallback is the real safety net; the retry is just for quick blips. */
const BACKOFF_BASE_MS = 250;

export type NestErrorKind =
  | "config" // VITE_API_URL not set
  | "auth" // no authenticated session
  | "timeout" // AbortSignal.timeout fired
  | "network" // fetch rejected (TypeError) — DNS/TLS/connection reset/CORS
  | "http"; // server responded with a non-2xx status

export class NestRequestError extends Error {
  readonly kind: NestErrorKind;
  readonly status?: number;
  readonly durationMs?: number;
  /** Correlation id (`x-request-id`) of the attempt that produced this error —
   *  quote it in fallback telemetry / error toasts to tie the FE report to the
   *  API log lines and Sentry event of that exact request. */
  readonly requestId?: string;
  /** Machine code from the Nest domain error body (e.g. "owner-004"), when present. */
  readonly code?: string;
  /** Bilingual message from the Nest domain error body — carried through so the
   *  UI renders the server's ar/en text (not just the Arabic `message`) in the
   *  active language. `getSupabaseErrorMessage` reads these first. */
  readonly ar?: string;
  readonly en?: string;
  /** Full URL that was attempted — attached to network-kind errors so the
   *  next D5 event carries the malformed string for diagnosis. */
  readonly url?: string;

  constructor(
    message: string,
    kind: NestErrorKind,
    opts?: {
      status?: number;
      durationMs?: number;
      requestId?: string;
      code?: string;
      ar?: string;
      en?: string;
      url?: string;
    },
  ) {
    super(message);
    this.name = "NestRequestError";
    this.kind = kind;
    this.status = opts?.status;
    this.durationMs = opts?.durationMs;
    this.requestId = opts?.requestId;
    this.code = opts?.code;
    this.ar = opts?.ar;
    this.en = opts?.en;
    this.url = opts?.url;
  }
}

export interface NestRequestOptions extends RequestInit {
  /** Override the per-attempt timeout (ms). Default 7000. */
  timeoutMs?: number;
  /** Override the retry count (after the first attempt). Default 2. */
  retries?: number;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * A failure worth retrying: transient network/timeout/5xx — never a 4xx.
 * Exported so callers can reuse the same classification for a react-query
 * `retry` predicate (e.g. `src/repositories/leads.ts` callers) instead of
 * duplicating the kind checks — see Sentry D5 (leads request failed).
 */
export function isRetryable(err: NestRequestError): boolean {
  if (err.kind === "network" || err.kind === "timeout") return true;
  if (err.kind === "http" && err.status !== undefined && err.status >= 500) return true;
  return false;
}

export interface MakeNestRequestOptions {
  /**
   * Forward the active role from `sessionStorage["dallali-active-role"]` as an
   * `x-active-role` header (the backend guard scopes by it) WHEN PRESENT. Opt-in
   * per slice. On its own this only attaches the header best-effort — it never
   * blocks the call. Pair with `requireActiveRole` for endpoints that actually
   * 403 without the header (see below).
   */
  forwardActiveRole?: boolean;
  /**
   * Only meaningful when `forwardActiveRole` is also true. When true, a missing
   * `sessionStorage["dallali-active-role"]` short-circuits BEFORE the fetch (and
   * before the session lookup) with the synthetic 403 shape `isRoleNotSetYet`
   * (nestFallback.ts) recognizes — mirroring the server's RowScopeGuard, which
   * genuinely 403s without the header (issue #3278).
   *
   * Set this ONLY for a binding whose target routes actually carry
   * RowScopeGuard (guarded READS). Do NOT set it on a binding shared with
   * WRITE routes — writes are never RowScopeGuard-protected (capability-gated
   * only) and would be wrongly blocked/rerouted to the Supabase fallback,
   * losing Nest-side behavior (e.g. write-path cache invalidation) with no
   * Sentry signal. Some slices (e.g. `expenses`) forward the role to a
   * service-layer scope resolver that degrades gracefully (returns `[]`) when
   * the role is absent instead of 403ing — those bindings want
   * `forwardActiveRole` alone, WITHOUT this flag.
   *
   * Default false: a plain `forwardActiveRole` binding never blocks a request
   * the server would happily answer (e.g. via a permission fast-path that
   * doesn't even consult the role).
   */
  requireActiveRole?: boolean;
  /**
   * Attach the staff Supabase JWT as `Authorization: Bearer`. Default true.
   * Set false for public/unauthenticated endpoints (marketplace/SEO) — no session
   * lookup is performed and no Authorization header is sent.
   */
  auth?: boolean;
  /**
   * Send `Content-Type: application/json`. Default true. Set false for multipart
   * (FormData) requests so the browser sets the `multipart/...; boundary=` header.
   */
  json?: boolean;
}

/** Idempotent methods are safe to retry; mutations are not (a retry after the
 *  server already committed but lost the response would double-write). */
function isIdempotentMethod(method: string): boolean {
  const m = method.toUpperCase();
  // Only auto-retry side-effect-free reads. PUT/DELETE are idempotent per the HTTP
  // spec, but we deliberately exclude them: many APIs implement PUT non-idempotently
  // (upsert-with-side-effects) and a retried DELETE 404s on the second attempt. PATCH
  // and POST are non-idempotent outright. A caller that knows a mutation is safe to
  // retry can opt in explicitly via the `retries` option.
  return m === "GET" || m === "HEAD" || m === "OPTIONS";
}

/**
 * Build a `nestRequest` bound to a repository slice (e.g. "contracts"). The slice
 * is used only for error messages — the returned function's signature matches the
 * old per-repo helper: `(path, options?) => Promise<T>`.
 */
export function makeNestRequest(slice: string, makeOpts?: MakeNestRequestOptions) {
  return async function nestRequest<T>(
    path: string,
    options?: NestRequestOptions,
  ): Promise<T> {
    if (!API_URL) {
      throw new NestRequestError(
        "Nest API unavailable: VITE_API_URL is not configured",
        "config",
      );
    }

    // Boot-race guard (issue #3278): RowScopeGuard-protected READS require
    // x-active-role, but on app load React Query hooks fire — gated only by
    // `!!companyId` — before the auth context has written the role to
    // sessionStorage. Short-circuit here, BEFORE the network fetch (and
    // before the session lookup below), and throw the exact
    // NestRequestError shape `isRoleNotSetYet` (nestFallback.ts) already
    // recognizes and suppresses. Gated on `requireActiveRole` (NOT just
    // `forwardActiveRole`) — see the option doc above: this must never engage
    // for a binding shared with WRITE routes, or for a binding whose reads
    // merely CONSULT the role without the server 403ing on its absence.
    if (
      makeOpts?.forwardActiveRole &&
      makeOpts?.requireActiveRole &&
      typeof window !== "undefined" &&
      !sessionStorage.getItem("dallali-active-role")
    ) {
      throw new NestRequestError("Missing x-active-role header", "http", {
        status: 403,
      });
    }

    const useAuth = makeOpts?.auth ?? true;
    const useJson = makeOpts?.json ?? true;

    const opts = options ?? {};
    const method = (opts.method ?? "GET").toString();
    // Retry idempotent reads by default; never auto-retry a mutation (avoids
    // double-writes on a lost-response blip). Callers can still override `retries`.
    const {
      timeoutMs = DEFAULT_TIMEOUT_MS,
      retries = isIdempotentMethod(method) ? DEFAULT_RETRIES : 0,
      ...init
    } = opts;

    let accessToken: string | undefined;
    if (useAuth) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      accessToken = session?.access_token;
      if (!accessToken) {
        throw new NestRequestError(
          `${slice}: an authenticated session is required`,
          "auth",
        );
      }
    }

    let attempt = 0;
    let lastError: NestRequestError | undefined;

    while (attempt <= retries) {
      const startedAt = performance.now();
      // Correlation id (fortress M8): a FRESH id per attempt, not per logical
      // request — each retry is a distinct server-side request, so reusing one
      // id would merge two API log streams and hide which attempt failed. The
      // thrown NestRequestError carries the id of the attempt that produced it.
      const requestId = crypto.randomUUID();
      // AbortController + setTimeout instead of AbortSignal.timeout for portability
      // (Safari/jsdom/older runtimes lack AbortSignal.timeout). The timer is created
      // inside the loop AFTER the auth/config guards above, so an early throw can't
      // leak it; once a fetch settles the `finally` clears it.
      const controller = new AbortController();
      const timer = setTimeout(
        () => controller.abort(new DOMException("Nest request timed out", "TimeoutError")),
        timeoutMs,
      );
      const activeRole =
        makeOpts?.forwardActiveRole && typeof window !== "undefined"
          ? sessionStorage.getItem("dallali-active-role")
          : null;

      try {
        // ── Build and sanitize headers (issue #3255 — Sentry DN) ──
        // Corrupted localStorage tokens can embed control characters or
        // non-Latin-1 bytes, which cause WebKit fetch() to throw
        // "The string did not match the expected pattern". Still a real
        // defense for DN, but NOT the cause of Sentry D5 ("leads request
        // failed (network)") — that theory is disproven (0
        // `nest-header-invalid` events in 14 days while D5 kept firing). D5
        // is a transient WebKit(Safari 26/iOS 26)-side network-layer
        // rejection on cross-origin fetches, unrelated to header content;
        // the fix is a query-level retry + visible degrade in the callers
        // (see `src/repositories/leads.ts` consumers), not header handling.
        const rawHeaders: Record<string, string> = {
          ...(useJson ? { "Content-Type": "application/json" } : {}),
          "X-API-Key": API_KEY,
          "x-request-id": requestId,
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          ...(activeRole ? { "x-active-role": activeRole } : {}),
          ...(init.headers as Record<string, string> | undefined),
        };

        // Validate every header value; report + self-heal on invalid.
        const headers: Record<string, string> = {};
        for (const [name, value] of Object.entries(rawHeaders)) {
          const str = String(value);
          const result = validateHeaderValue(str);
          if (result.valid) {
            headers[name] = str;
            continue;
          }

          Sentry.captureMessage("nest-header-invalid", {
            level: "warning",
            tags: {
              header: name,
              reason: result.reason,
              value_length: String(str.length),
            },
            fingerprint: [`nest-header-invalid-${name}`],
          });

          if (name.toLowerCase() === "authorization") {
            // Corrupted Bearer token — clear stored Nest tokens so the
            // 401 path on the next request (or next auth check) re-issues
            // a fresh session instead of reloading the same corrupt token.
            clearNestRefreshToken();
            clearNestAccessToken();
            // Proceed WITHOUT Authorization — the 401 path handles re-auth.
          }
          // Non-Authorization header: drop it, continue.
        }

        const res = await fetch(`${API_URL}${path}`, {
          ...init,
          signal: controller.signal,
          headers,
        });

        if (!res.ok) {
          // Nest domain errors carry a bilingual body `{ code, ar, en, message }`
          // (see apps/api/src/**/errors/*). Thread ar/en/code through so the UI
          // renders the server's message in the active language, not just `message`.
          const body = (await res.json().catch(() => ({}))) as {
            message?: string;
            code?: string;
            ar?: string;
            en?: string;
          };
          const err = new NestRequestError(
            body.message || `${slice} request failed (${res.status})`,
            "http",
            {
              status: res.status,
              durationMs: Math.round(performance.now() - startedAt),
              requestId,
              code: typeof body.code === "string" ? body.code : undefined,
              ar: typeof body.ar === "string" ? body.ar : undefined,
              en: typeof body.en === "string" ? body.en : undefined,
            },
          );
          if (isRetryable(err) && attempt < retries) {
            lastError = err;
            attempt += 1;
            await sleep(BACKOFF_BASE_MS * attempt);
            continue;
          }
          throw err;
        }

        if (res.status === 204 || res.status === 205) return undefined as T;
        try {
          return (await res.json()) as T;
        } catch (parseErr) {
          // A 2xx with an empty (or whitespace-only) body makes res.json()
          // throw "Unexpected end of JSON input". Treat it like a 204 →
          // undefined, instead of letting it fall through to the outer catch
          // and be mis-classified as a retryable network error
          // (Sentry DALLALI-FRONTEND-D7 / #2897). A genuinely malformed,
          // non-empty body still surfaces unchanged.
          // Match the empty-body signature across JS engines: V8 / Node
          // ("Unexpected end of JSON input"), SpiderMonkey / Firefox
          // ("unexpected end of data"), JavaScriptCore / Safari
          // ("Unexpected EOF"). A malformed, non-empty body ("Unexpected
          // token …") is NOT matched and still surfaces.
          if (
            parseErr instanceof SyntaxError &&
            /unexpected end of|unexpected eof/i.test(parseErr.message)
          ) {
            return undefined as T;
          }
          throw parseErr;
        }
      } catch (e) {
        // Re-throw a non-retryable HTTP error untouched.
        if (e instanceof NestRequestError) {
          if (e.kind === "http" && !(isRetryable(e) && attempt < retries)) throw e;
          lastError = e;
        } else {
          // fetch() rejection: TimeoutError (our abort) or TypeError (network).
          const name = (e as Error)?.name;
          const kind: NestErrorKind =
            name === "TimeoutError" || name === "AbortError" ? "timeout" : "network";
          lastError = new NestRequestError(
            `${slice} request failed (${kind}): ${(e as Error)?.message ?? "unknown"}`,
            kind,
            { durationMs: Math.round(performance.now() - startedAt), requestId, url: `${API_URL}${path}` },
          );
          if (!isRetryable(lastError)) throw lastError;
        }

        if (attempt >= retries) throw lastError;
        attempt += 1;
        await sleep(BACKOFF_BASE_MS * attempt);
      } finally {
        clearTimeout(timer);
      }
    }

    // Unreachable in practice — the loop either returns or throws.
    throw lastError ?? new NestRequestError(`${slice} request failed`, "network");
  };
}

/**
 * Fire-and-forget POST to a Nest endpoint. NEVER throws — failure is
 * swallowed so the caller's surrounding mutation (which has already committed
 * the row) can still fire its success toast.
 *
 * Returns silently when the Nest API isn't configured (Supabase-only
 * environments don't have the translation worker, so the record just stays
 * in `translation_state = 'pending'`).
 *
 * Prefer this over bare fetch() copies in repositories — it shares the
 * auth token resolution and API-key header with makeNestRequest.
 *
 * Failures are reported to Sentry at `warning` level (not error): the row IS
 * persisted, only the async enrichment was missed, and a re-save or the backfill
 * job recovers it. Reporting it (rather than only `console.warn`) means a broken
 * translation worker surfaces as a spike instead of failing silently in prod.
 * BOTH failure modes are reported: a non-2xx response (worker up but erroring —
 * `fetch` does NOT throw on those) and a network-level rejection (DNS/connection
 * refused — `fetch` rejects).
 *
 * @param path  — Route suffix (e.g. API_ROUTES.properties.translate(id)).
 * @param label — Human label for the warning on failure (e.g. "units.translate").
 */
export async function fireAndForgetNestPost(
  path: string,
  label: string,
): Promise<void> {
  if (!API_URL) return;
  // Minted outside the try so the catch path can tag its Sentry event with it —
  // a network-level rejection (server may never have seen the request) is the
  // failure mode that most needs correlation.
  const requestId = crypto.randomUUID();
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const accessToken = session?.access_token;
    if (!accessToken) return;

    // ── Sanitize headers (issue #3255) ──
    const rawHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      "X-API-Key": API_KEY,
      "x-request-id": requestId,
      Authorization: `Bearer ${accessToken}`,
    };
    const headers: Record<string, string> = {};
    for (const [name, value] of Object.entries(rawHeaders)) {
      const str = String(value);
      const result = validateHeaderValue(str);
      if (result.valid) {
        headers[name] = str;
        continue;
      }
      Sentry.captureMessage("nest-header-invalid", {
        level: "warning",
        tags: {
          header: name,
          reason: result.reason,
          value_length: String(str.length),
        },
        fingerprint: [`nest-header-invalid-${name}`],
      });
      if (name.toLowerCase() === "authorization") {
        clearNestRefreshToken();
        clearNestAccessToken();
        // Without a valid token, abort — the fire-and-forget can't auth.
        return;
      }
      // Non-Authorization: drop header, continue.
    }

    const res = await fetch(`${API_URL}${path}`, {
      method: "POST",
      headers,
    });
    // A 4xx/5xx does NOT reject fetch — a worker that's up but erroring would
    // otherwise be swallowed silently. Report it (still fire-and-forget).
    if (!res.ok) {
      console.warn(`${label}: translate POST returned ${res.status}`);
      Sentry.captureMessage(`${label}: translate POST returned ${res.status}`, {
        level: "warning",
        tags: {
          transport: "fireAndForgetNestPost",
          label,
          status: String(res.status),
          request_id: requestId,
        },
      });
    }
  } catch (err) {
    console.warn(`${label}: translate POST failed`, err);
    Sentry.captureException(err, {
      level: "warning",
      tags: { transport: "fireAndForgetNestPost", label, request_id: requestId },
    });
  }
}
